import { LogLevel } from '../constants';
import { getClientMetadata } from '../utils';
import { LoggerChain } from './core/logger-chain';
import { InvalidLogLevelError, isValidLogType } from './core/utils';
import { LoggerAdapter, LoggerMetadata, LogOptions } from './types';

/**
 * Logger class that provides a structured, chainable, and extensible logging API.
 *
 * This class allows logging messages with various severity levels (`trace`, `debug`,
 * `info`, `warn`, `error`, `fatal`) and supports setting global metadata.
 *
 * It follows a **chainable pattern**, allowing flexible usage such as:
 *
 * ```ts
 * logger.info('Application started');
 * logger.error(new Error('Critical failure')).options({ silent: true });
 * logger.options({ silent: true }).debug('User data', user);
 * ```
 */
export class Logger {
  private adapter: LoggerAdapter;

  /**
   * Private constructor to enforce instantiation through `Logger.create()`.
   *
   * @param {LoggerAdapter} adapter - The logging adapter implementation.
   */
  private constructor(adapter: LoggerAdapter) {
    this.adapter = adapter;
  }

  /**
   * Factory Static Method to create a new Logger instance.
   *
   * This method accepts a logging adapter and returns a new Logger instance.
   *
   * @param {LoggerAdapter} adapter - The logging adapter implementation.
   * @returns {Logger} A new Logger instance.
   *
   * @example
   * ```ts
   * const logger = Logger.create(new PinoAdapter(config));
   * ```
   */
  public static create(adapter: LoggerAdapter): Logger {
    const logger = new Logger(adapter);
    // Set initial metadata (e.g., account and client details).
    logger.setMetadata({
      account: null,
      client: getClientMetadata(),
    });
    return logger;
  }

  /**
   * Creates a child logger with additional context.
   *
   * The child logger inherits the parent's configuration and metadata.
   *
   * @param {string} name - The context to append to the logger's source.
   * @param {LoggerMetadata} [metadata={}] - Additional metadata for the child logger.
   * @returns {Logger} A new child Logger instance.
   *
   * @example
   * ```ts
   * const userLogger = logger.child('UserService');
   * userLogger.info('Fetching user data');
   * ```
   */
  public child(name: string, metadata: LoggerMetadata = {}): Logger {
    return new Logger(this.adapter.child(name, metadata));
  }

  /**
   * Creates a lazy child logger with delayed initialization.
   *
   * The lazy child logger is a proxy that initializes the child logger only when its methods are first accessed.
   *
   * @param {string} name - The context name for the lazy child logger.
   * @param {LoggerMetadata} [metadata={}] - Additional metadata for the lazy child logger.
   * @returns {Logger} A new lazy child Logger instance.
   *
   * @example
   * ```ts
   * const lazyLogger = logger.lazyChild('Deferred');
   * lazyLogger.info('This is executed only when used');
   * ```
   */
  public lazyChild(name: string, metadata: LoggerMetadata = {}): Logger {
    let instance: Logger;
    return new Proxy({} as Logger, {
      get: (_, prop, receiver) => {
        if (!instance) {
          instance = this.child(name, metadata);
        }
        return Reflect.get(instance, prop, receiver);
      },
    });
  }

  /**
   * Sets or updates the metadata for the logger.
   *
   * The provided metadata is merged with existing global metadata and included in every log entry.
   *
   * @param {LoggerMetadata} metadata - The metadata to set.
   *
   * @example
   * ```ts
   * logger.setMetadata({ account: '1234', user: 'admin' });
   * ```
   */
  public setMetadata(metadata: LoggerMetadata): void {
    this.adapter.setMetadata(metadata);
  }

  /**
   * Sets the log level.
   *
   * @param {LogLevel} level - The new log level to be set.
   * @throws {Error} Throws an error if the provided log level is invalid.
   */
  public setLevel(level: LogLevel): void {
    if (!isValidLogType(level)) {
      throw new InvalidLogLevelError(level);
    }
    this.adapter.setLevel(level);
  }

  /**
   * Creates a new instance of LoggerChain for chaining log calls with optional settings.
   *
   * @param {LogOptions} [initialOptions] - Optional log options.
   * @returns {LoggerChain} A new LoggerChain instance.
   */
  private createChain(initialOptions?: LogOptions): LoggerChain {
    return new LoggerChain(this.adapter, initialOptions);
  }

  /**
   * Logs a **trace-level** message for deep debugging.
   *
   * Use this for very detailed logs that help trace the execution flow.
   *
   * @param {...any} args - The log message and optional additional data.
   * @returns {LoggerChain} A chainable logger instance.
   *
   * @example
   * ```ts
   * logger.trace('Function execution trace', { function: 'fetchData' });
   * ```
   */
  public trace(...args: any[]): LoggerChain {
    return this.createChain().trace(...args);
  }

  /**
   * Logs a **debug-level** message for troubleshooting.
   *
   * Use this for diagnostic information useful during development.
   *
   * @param {...any} args - The log message and optional additional data.
   * @returns {LoggerChain} A chainable logger instance.
   *
   * @example
   * ```ts
   * logger.debug('User session data', session);
   * ```
   */
  public debug(...args: any[]): LoggerChain {
    return this.createChain().debug(...args);
  }

  /**
   * Logs an **info-level** message for general system events.
   *
   * Use this for regular information that tracks system operations.
   *
   * @param {...any} args - The log message and optional additional data.
   * @returns {LoggerChain} A chainable logger instance.
   *
   * @example
   * ```ts
   * logger.info('User logged in', { userId: 123 });
   * ```
   */
  public info(...args: any[]): LoggerChain {
    return this.createChain().info(...args);
  }

  /**
   * Logs a **warning-level** message to indicate potential issues.
   *
   * Use this when an unexpected event occurs but the application can continue operating.
   *
   * @param {...any} args - The log message and optional additional data.
   * @returns {LoggerChain} A chainable logger instance.
   *
   * @example
   * ```ts
   * logger.warn('API response took too long', { duration: 5000 });
   * ```
   */
  public warn(...args: any[]): LoggerChain {
    return this.createChain().warn(...args);
  }

  /**
   * Logs an **error-level** message for recoverable failures.
   *
   * Use this when an error occurs that does not require immediate termination of the application.
   *
   * @param {...any} args - The log message and optional additional data.
   * @returns {LoggerChain} A chainable logger instance.
   *
   * @example
   * ```ts
   * logger.error(new Error('Database connection lost'));
   * ```
   */
  public error(...args: any[]): LoggerChain {
    return this.createChain().error(...args);
  }

  /**
   * Logs a **fatal-level** message for critical failures.
   *
   * Use this when an unrecoverable error occurs and the system must be terminated.
   *
   * @param {...any} args - The log message and optional additional data.
   * @returns {LoggerChain} A chainable logger instance.
   *
   * @example
   * ```ts
   * logger.fatal('Critical system failure', { code: 'SYSTEM_CRASH' });
   * ```
   */
  public fatal(...args: any[]): LoggerChain {
    return this.createChain().fatal(...args);
  }

  /**
   * Sets the log options for the current chain.
   *
   * This method allows modifying log behavior (e.g., silencing the console output)
   * before calling a log method.
   *
   * @param {LogOptions} opts - The log options to apply.
   * @returns {LoggerChain} A chainable logger instance.
   *
   * @example
   * ```ts
   * logger.options({ silent: true }).error(new Error('Silent error'));
   * ```
   */
  public options(opts: LogOptions): LoggerChain {
    return this.createChain(opts);
  }
}
